import { Link } from "react-router-dom";
import Categories from "@/components/Categories";
import HeroBanner from "@/components/HeroBanner";
import { ShieldCheck, Truck, HeartHandshake, BadgeCheck, ChevronRight, Menu } from "lucide-react";
import { useDesignProducts } from "@/hooks/useDesignProducts";
import { useSiteContent } from "@/hooks/useSiteContent";
import { useMenuCategories } from "@/hooks/useCategories";
import SectionHeader from "./SectionHeader";
import ProductRow from "./ProductRow";
import BabyCareTipsSection from "./BabyCareTipsSection";




const Home = () => {
  const { content } = useSiteContent();
  const brandName = content.brand_name || "BabyBite";
  const { data: menuCategories = [] } = useMenuCategories();
  const { newProducts, bestProducts, allProducts } = useDesignProducts({
    designId: "home",
    newLimit: 7,
    bestLimit: 7,
    allLimit: 7,
  });

  const featuredItems = newProducts.slice(0, 4);
  const bestItems = bestProducts.slice(0, 4);
  const collectionItems = allProducts.slice(0, 4);
  const hasMoreFeatured = newProducts.length > 6;
  const hasMoreBest = bestProducts.length > 6;
  const hasMoreAll = allProducts.length > 6;

  return (
    <div className="bg-background text-foreground">
      {/* HERO with side categories (desktop) */}
      <section data-section-id="home" className="bg-gradient-to-br from-[hsl(var(--baby-peach))] via-[hsl(var(--baby-cream))] to-[hsl(var(--baby-lavender))]">
        <div className="grid w-full items-stretch lg:grid-cols-[280px_1fr]">
          {/* Categories sidebar — desktop only */}
          <aside className="hidden h-full self-stretch lg:block">
            <div className="flex h-full max-h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[0_8px_30px_-12px_hsl(var(--primary)/0.18)] ring-1 ring-black/5">
              <div className="relative flex items-center gap-2.5 bg-primary px-5 py-4 text-primary-foreground">
                <Menu size={17} strokeWidth={2.25} />
                <h2 className="font-heading text-[13px] font-semibold uppercase tracking-[0.18em]">Our Categories</h2>
                <span className="absolute inset-x-5 bottom-0 h-px bg-gradient-to-r from-transparent via-primary-foreground/40 to-transparent" />
              </div>

              <ul className="min-h-0 flex-1 divide-y divide-border/60 overflow-hidden">
                {menuCategories.slice(0, 7).map((cat) => (
                  <li key={cat.id} className="group/cat">
                    <Link
                      to={`/category/${cat.slug}`}
                      className="relative flex items-center justify-between px-5 py-[13px] text-[13px] font-medium text-foreground/85 transition-colors hover:bg-secondary hover:text-primary"
                    >
                      <span className="truncate tracking-wide">{cat.name}</span>
                      <ChevronRight size={15} className="text-muted-foreground/60 transition-all group-hover/cat:translate-x-0.5 group-hover/cat:text-primary" />
                    </Link>
                  </li>
                ))}
                {menuCategories.length === 0 && (
                  <li className="px-5 py-3 text-sm text-muted-foreground">No categories available</li>
                )}
              </ul>

              <Link
                to="/categories"
                className="group/all relative flex items-center justify-between gap-2 border-t border-border bg-primary px-5 py-3.5 text-[12px] font-semibold uppercase tracking-[0.16em] text-primary-foreground transition-all hover:bg-primary/90"
              >
                <span>View All Categories</span>
                <ChevronRight size={15} className="transition-transform group-hover/all:translate-x-1" />
              </Link>
            </div>
          </aside>
          {/* Hero banner */}
          <div className="min-w-0">
            <HeroBanner />
          </div>
        </div>
      </section>

      <Categories />


      {/* FEATURED */}
      <section data-section-id="new_arrivals" className="bg-secondary py-12 md:py-20">
        <div className="mx-auto max-w-[1800px] px-3 md:px-6">
          <SectionHeader title="Featured Products" viewAllHref={hasMoreFeatured ? "/new-arrivals" : undefined} viewAllLabel="View All" />
          <ProductRow products={featuredItems} />
          {hasMoreFeatured && (
            <div className="mt-6 flex justify-center md:hidden">
              <Link to="/new-arrivals" className="whitespace-nowrap border-b border-primary pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground transition-colors hover:text-primary">
                View All
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* BEST SELLERS */}
      <section data-section-id="best_sellers" className="bg-background py-12 md:py-20">
        <div className="mx-auto max-w-[1800px] px-3 md:px-6">
          <SectionHeader title="Best Sellers" viewAllHref={hasMoreBest ? "/best-selling" : undefined} viewAllLabel="View All" />
          <ProductRow products={bestItems} />
          {hasMoreBest && (
            <div className="mt-6 flex justify-center md:hidden">
              <Link to="/best-selling" className="whitespace-nowrap border-b border-primary pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground transition-colors hover:text-primary">
                View All
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="bg-gradient-to-br from-[hsl(var(--baby-cream))] to-[hsl(var(--baby-pink))] py-6 md:py-10">
        <div className="mx-auto max-w-[1800px] px-3 md:px-6">
          <div className="mb-4 text-center md:mb-6">
            <h2 className="font-heading text-lg text-foreground md:text-2xl">Why Choose {brandName}?</h2>
            <p className="mt-0.5 text-[11px] text-muted-foreground md:text-sm">Your baby's safety and comfort are our priority</p>
          </div>
          <div className="grid grid-cols-4 gap-2 md:gap-4">
            {[
              { icon: ShieldCheck, title: "100% Safe", desc: "Safe & tested" },
              { icon: BadgeCheck, title: "Original", desc: "Top quality" },
              { icon: Truck, title: "Fast Delivery", desc: "All Bangladesh" },
              { icon: HeartHandshake, title: "Cash on Delivery", desc: "Pay on receive" },
            ].map((f, i) => (
              <div key={i} className="rounded-xl bg-card p-2 text-center shadow-sm md:p-4">
                <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary md:h-11 md:w-11">
                  <f.icon size={16} className="md:hidden" />
                  <f.icon size={20} className="hidden md:block" />
                </div>
                <h3 className="mt-1.5 text-[11px] font-semibold leading-tight text-foreground md:mt-2 md:text-sm">{f.title}</h3>
                <p className="mt-0.5 text-[9px] leading-tight text-muted-foreground md:text-xs">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ALL PRODUCTS */}
      <section data-section-id="all_collections" className="bg-background py-12 md:py-20">
        <div className="mx-auto max-w-[1800px] px-3 md:px-6">
          <SectionHeader title="All Products" viewAllHref={hasMoreAll ? "/products" : undefined} viewAllLabel="View All" />
          <ProductRow products={collectionItems} />
          {hasMoreAll && (
            <div className="mt-6 flex justify-center md:hidden">
              <Link to="/products" className="whitespace-nowrap border-b border-primary pb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-foreground transition-colors hover:text-primary">
                View All
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* BABY CARE TIPS — Articles (last section before footer) */}
      <BabyCareTipsSection />
    </div>
  );
};

export default Home;